import { StoreApi } from 'zustand';
import { ChangeModel } from '../../model/change.model';
import { DiagramState } from '../diagram.store';
import { Operation, OperationId } from '../../model/operation.model';
import { v7 as uuidv7 } from 'uuid';
import { calculateInverse } from '../../utils/inverse-change';

export interface BrokerSlice {
  //record of all operations
  ledger: Map<OperationId, Operation>;
  // operations waiting to be processed
  outbox: Map<OperationId, Operation>;

  // undo/redo set pointers
  undoSet: Set<OperationId>;
  // stores the undo operation and its inverse operation id
  redoMap: Map<OperationId, [OperationId, OperationId]>;

  currentOpId: string | null;
  currentSequence: number; // monotonic sequence for tracking first / last change across multiple elements
  currentChanges: ChangeModel[];

  // write a change to the ledger and outbox
  recordOperation: (changes: ChangeModel[], elements?: Set<string>) => void;

  // functions to create and perform operations
  startOperation: (changes: ChangeModel[]) => void;
  addChangesToCurrentOperation: (changes: ChangeModel[]) => void;
  finalizeOperation: (changes: ChangeModel[]) => void;

  // undo / redo utilities
  undo: () => void;
  redo: () => void;
}

export const createBrokerSlice = (set: StoreApi<DiagramState>['setState']) =>
  ({
    ledger: new Map(),
    outbox: new Map(),

    undoSet: new Set(),
    redoMap: new Map(),

    currentOpId: null,
    currentSequence: 0,
    currentChanges: [],
    startOperation: (changes: ChangeModel[], elements?: Set<string>) => {
      const opId = uuidv7();
      let elementIds = new Set<string>();
      if (!elements) {
        changes.forEach((change: ChangeModel) => {
          if ('id' in change) {
            elementIds.add(change.id);
          }
        });
      } else {
        elementIds = elements;
      }

      set(() => ({
        currentOpId: opId,
        currentElements: elementIds,
        currentChanges: changes,
      }));
    },
    addChangesToCurrentOperation: (changes) => {
      set((s: DiagramState) => ({
        currentChanges: [...s.currentChanges, ...changes],
      }));
    },
    finalizeOperation: (changes) => {
      set((s: DiagramState) => {
        if (!s.currentOpId) {
          console.error('No current operation to finalize');
          return s;
        }
        const operation: Operation = {
          opId: s.currentOpId as OperationId,
          clientId: s.clientId,
          changes: [...s.currentChanges, ...changes],
          timestamp: Date.now(),
        };

        return {
          ledger: new Map(s.ledger).set(operation.opId, operation),
          outbox: new Map(s.outbox).set(operation.opId, operation),
          currentOpId: null,
          currentChanges: [],
          undoSet: new Set(s.undoSet).add(operation.opId),
        };
      });
    },

    recordOperation: (changes) => {
      set((s: DiagramState) => {
        const opId = uuidv7();
        const operation: Operation = {
          opId: opId as OperationId,
          clientId: s.clientId,
          changes,
          timestamp: Date.now(),
        };

        const newOutbox = new Map(s.outbox).set(opId as OperationId, operation);

        const newLedger = new Map(s.ledger).set(opId as OperationId, operation);

        return {
          undoSet: new Set(s.undoSet).add(opId as OperationId),
          ledger: newLedger,
          outbox: newOutbox,
        };
      });
    },
    undo: () => {
      set((s: DiagramState) => {
        if (s.undoSet.size === 0) {
          console.warn('No operations to undo');
          return s;
        }
        const lastOpId = Array.from(s.undoSet).pop();
        if (!lastOpId) {
          console.warn('No operation to undo');
          return s;
        }
        const operation = s.ledger.get(lastOpId);
        if (!operation) {
          console.warn('Operation not found');
          return s;
        }

        const inverseOperation = calculateInverse(operation);

        s.applyOperationChanges(inverseOperation.changes);

        const copiedOutbox = new Map(s.outbox);
        copiedOutbox.delete(lastOpId);
        copiedOutbox.set(inverseOperation.opId, inverseOperation);

        const newUndoSet = new Set(s.undoSet);
        newUndoSet.delete(lastOpId);

        const newRedoMap = new Map(s.redoMap);
        newRedoMap.set(lastOpId, [lastOpId, inverseOperation.opId]);

        // Apply the inverse of the changes
        // Move from undo to redo
        return {
          undoSet: newUndoSet,
          redoMap: newRedoMap,
          ledger: new Map(s.ledger).set(
            inverseOperation.opId,
            inverseOperation
          ),
          outbox: copiedOutbox,
        };
      });
    },
    redo: () => {
      set((s: DiagramState) => {
        if (s.redoMap.size === 0) {
          console.warn('No operations to redo');
          return s;
        }
        const lastOpId = [...s.redoMap.keys()].pop();
        if (!lastOpId) {
          console.warn('No operation to redo');
          return s;
        }
        const operation = s.ledger.get(lastOpId);
        if (!operation) {
          console.warn('Operation not found');
          return s;
        }

        s.applyOperationChanges(operation.changes);

        const inverseOperationId = s.redoMap.get(lastOpId)![1];

        const newOutbox = new Map(s.outbox);
        newOutbox.delete(inverseOperationId);
        newOutbox.set(lastOpId, operation);

        const newRedoMap = new Map(s.redoMap);
        newRedoMap.delete(lastOpId);

        // Move from redo to undo
        return {
          outbox: newOutbox,
          redoMap: newRedoMap,
          undoSet: new Set(s.undoSet).add(lastOpId),
        };
      });
    },
  } satisfies BrokerSlice);
